import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../services/api';
import { notificationTarget } from '../utils/notificationTarget';
import { fmtDate } from '../utils/dateUtils';
import InstallButton from './InstallButton';

export default function Navbar({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [dropOpen, setDropOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('notif_sound') !== 'off');
  const notifRef = useRef(null);
  const lastMaxId = useRef(null);
  const audioRef = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  const playBeep = useCallback(() => {
    if (!soundRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = audioRef.current || (audioRef.current = new Ctx());
      if (ctx.state === 'suspended') ctx.resume();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start();
      o.stop(ctx.currentTime + 0.37);
    } catch { /* ignore */ }
  }, []);

  const alertNew = useCallback((fresh) => {
    const top = fresh[0];
    if (!top) return;
    playBeep();
    setToast(top);
    setTimeout(() => setToast((t) => (t && t.id === top.id ? null : t)), 6000);
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const bn = new Notification(top.title, { body: top.message, icon: '/icon-192.png', tag: `tt-${top.id}` });
        bn.onclick = () => {
          window.focus();
          const t = notificationTarget(top, userRef.current?.role);
          if (t) navigate(t);
          bn.close();
        };
      }
    } catch { /* ignore */ }
  }, [navigate, playBeep]);

  const load = useCallback(() => {
    notificationsAPI.list().then(({ data }) => {
      setNotifs(data);
      const maxId = data.reduce((m, n) => Math.max(m, n.id), 0);
      if (lastMaxId.current === null) { lastMaxId.current = maxId; return; }  // baseline
      if (maxId > lastMaxId.current) {
        const fresh = data.filter((n) => n.id > lastMaxId.current && !n.is_read);
        lastMaxId.current = maxId;
        if (fresh.length) alertNew(fresh);
      }
    }).catch(() => {});
  }, [alertNew]);

  useEffect(() => {
    // Ask for OS notification permission once
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  // Close the notification dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggleSound = () => {
    setSoundOn((on) => { localStorage.setItem('notif_sound', on ? 'off' : 'on'); return !on; });
  };

  const openToast = () => {
    const n = toast; setToast(null);
    if (!n) return;
    const t = notificationTarget(n, user?.role);
    if (t) navigate(t);
  };

  const unread = notifs.filter((n) => !n.is_read).length;

  const openNotif = async (n) => {
    setNotifOpen(false);
    if (!n.is_read) {
      try { await notificationsAPI.markRead(n.id); } catch { /* ignore */ }
      setNotifs((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
    }
    const target = notificationTarget(n, user?.role);
    if (target) navigate(target);
  };

  const markAll = async () => {
    try { await notificationsAPI.markAllRead(); } catch { /* ignore */ }
    setNotifs((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const roleBadge = {
    super_admin: { label: 'Super Admin', cls: 'badge-purple' },
    hr: { label: 'HR', cls: 'badge-blue' },
    employee: { label: 'Employee', cls: 'badge-green' },
  };
  const rb = roleBadge[user?.role] || {};

  return (
    <header className="navbar">
      <div className="navbar-left">
        <button className="menu-toggle" onClick={onToggleSidebar} aria-label="Toggle sidebar">☰</button>
        <span className="navbar-title">Task &amp; Work Report System</span>
      </div>

      <div className="navbar-right">
        <InstallButton />

        <div className="notif-wrap" ref={notifRef}>
          <button className="notif-btn" aria-label="Notifications" onClick={() => setNotifOpen((o) => !o)}>
            🔔
            {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
          </button>

          {notifOpen && (
            <div className="notif-dropdown">
              <div className="notif-dropdown-head">
                <strong>Notifications</strong>
                <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button className="notif-sound-toggle" title={soundOn ? 'Mute alert sound' : 'Enable alert sound'}
                    onClick={toggleSound}>{soundOn ? '🔊' : '🔇'}</button>
                  {unread > 0 && <button className="link-sm" onClick={markAll}>Mark all read</button>}
                </span>
              </div>
              <div className="notif-dropdown-list">
                {notifs.length === 0 ? (
                  <div className="notif-empty">No notifications</div>
                ) : notifs.slice(0, 12).map((n) => (
                  <button key={n.id} className={`notif-row${n.is_read ? '' : ' unread'}`} onClick={() => openNotif(n)}>
                    <div className="notif-row-title">{n.title}</div>
                    <div className="notif-row-msg">{n.message}</div>
                    <div className="notif-row-time">{fmtDate(n.created_at)}</div>
                  </button>
                ))}
              </div>
              <Link to="/notifications" className="notif-dropdown-foot" onClick={() => setNotifOpen(false)}>
                View all
              </Link>
            </div>
          )}
        </div>

        <div className="user-menu" onClick={() => setDropOpen(!dropOpen)}>
          <div className="avatar">{user?.full_name?.[0]?.toUpperCase()}</div>
          <div className="user-info">
            <span className="user-name">{user?.full_name}</span>
            <span className={`role-badge ${rb.cls}`}>{rb.label}</span>
          </div>
          <span className="chevron">{dropOpen ? '▲' : '▼'}</span>
        </div>

        {dropOpen && (
          <div className="user-dropdown">
            <div className="dropdown-header">
              <strong>{user?.full_name}</strong>
              <small>{user?.email}</small>
            </div>
            <button className="dropdown-item danger" onClick={handleLogout}>Sign Out</button>
          </div>
        )}
      </div>

      {toast && (
        <div className="notif-toast" onClick={openToast} role="alert">
          <div className="notif-toast-icon">🔔</div>
          <div className="notif-toast-body">
            <div className="notif-toast-title">{toast.title}</div>
            <div className="notif-toast-msg">{toast.message}</div>
          </div>
          <button className="notif-toast-close" onClick={(e) => { e.stopPropagation(); setToast(null); }}>×</button>
        </div>
      )}
    </header>
  );
}
